import React, { useState, useEffect, useRef } from 'react';
import produce from 'immer';
import * as queryString from 'query-string';
import { useHistory, useLocation } from 'react-router-dom';
import { Col, Nav, NavItem, NavLink, Row, TabContent, TabPane, Button } from 'reactstrap';
import swal from 'sweetalert';
import * as applicantService from '../Application/Services/applicantService';
import ContentWrapper from '../Layout/ContentWrapper';
import ApplicantBasicCard from './Components/shared/applicantBasicCard';
import ApplicantDetails from './Components/shared/applicantDetails';
import CommentsCard from './Components/shared/commentsCard';
import * as utils from './Services/utils';
import ApplicationsTable from './Components/Tables/applicationsTable';
import { SpinnerLoader } from './Components/shared/spinnerLoader';
import { useTranslation } from 'react-i18next';
import { downloadDocument } from './Helpers/applicantHelper';
import CommentsAndHistoryTimeline from './Components/Timelines/commentsandHistoryTimeline';
import { getApplication, getApplicationBYJobAndApplicant } from './Services/applicationService';
import ApplicationDisplay from './applicationDisplay';
import { addRatingToApplicantsAnswer } from '../Application/Services/applicantService';

const ApplicationDetails = () => {
  const history = useHistory();
  const location = useLocation();
  const { t, i18n } = useTranslation('translations');
  const fileDownloadRef = useRef(null);
  // const [score, setScore] = useState(0);

  // Local state (initializing with similar defaults as before)
  const [activeTab, setActiveTab] = useState('applicationDetails');
  const [application, setApplication] = useState({
    applicant: {
      comments: [{}],
      documents: [{}],
      profilePicture: {},
      applications: [{}],
    },
    job: {
      jobCompetences: [
        {
          competence: {},
        },
      ],
    },
    score: { applicantCompetenceAnalisys: [{}] },
    applicantJobCompetences: [{}],
  });
  const [applicantId, setApplicantId] = useState(0);
  const [loading, setLoading] = useState(true);
  const [fileName] = useState('applicationDetails.jpg');
  const [applicantHistory, setApplicantHistory] = useState(null);

  // Ref to track previous location.search for updates
  const prevLocationSearchRef = useRef(location.search);

  // Function to combine and sort applicant history data
  const combineAndSortApplicantHistory = (jsonData) => {
    const comments = jsonData.comments.map((comment) => ({
      ...comment,
      type: 'comment',
    }));
    const applicantEvents = jsonData.applicantEvents.map((event) => ({
      ...event,
      type: 'event',
    }));
    const combinedArray = [...comments, ...applicantEvents];
    combinedArray.sort((a, b) => new Date(b.created) - new Date(a.created));
    return combinedArray;
  };

  // Prepare applicant history using a provided applicant ID (or fallback)
  const prepareApplicantHistory = async (idParam) => {
    setLoading(true);
    let id = idParam || applicantId;
    if (!id) {
      const parsed = queryString.parse(location.search);
      id = parsed.applicantId;
    }
    const { data: historyData } = await applicantService.getApplicantHistory(id);
    const sortedHistory = combineAndSortApplicantHistory(historyData);
    setApplicantHistory(sortedHistory);
    setLoading(false);
  };

  // Refresh the data based on the URL query parameters
  const refreshData = async () => {
    setLoading(true);
    const parsed = queryString.parse(location.search);

    if (parsed.applicationId) {
      // Came from ApplicationsTable – searching by applicationId
      const { data: applicationData } = await getApplication(parsed.applicationId);
      setApplication(applicationData);
      setApplicantId(applicationData.applicantId);
      setLoading(false);
      prepareApplicantHistory(applicationData.applicantId);
    } else if (parsed.jobId) {
      // Coming from applicantsTable – using jobId and applicantId
      const { data: applicationData } = await getApplicationBYJobAndApplicant(parsed.jobId, parsed.applicantId);
      setApplication(applicationData);
      setActiveTab('applicantDetails');
      setApplicantId(applicationData.applicantId);
      setLoading(false);
      prepareApplicantHistory(applicationData.applicantId);
    } else if (parsed.applicantId) {
      const { data: applicant } = await applicantService.getApplicant(parsed.applicantId);
      // Using produce to update nested state if needed
      setApplication((prev) =>
        produce(prev, (draft) => {
          draft.applicant = applicant;
        })
      );
      setApplicantId(parsed.applicantId);
      setLoading(false);
      prepareApplicantHistory(parsed.applicantId);
    }
  };

  const updateFeOnly = async (applicantUpdates) => {
    setApplication((prev) => ({
      ...prev,
      applicant: { ...prev.applicant, ...applicantUpdates },
    }));
  };

  // Toggle the active tab and update the URL query accordingly
  const toggleTab = (tab) => {
    if (activeTab !== tab) {
      const searchParams = new URLSearchParams(location.search);
      searchParams.set('tab', tab);
      history.push({ search: searchParams.toString() });
      setActiveTab(tab);
    }
  };

  // Add or update a comment in the applicant history
  const addOrUpdateCommentToApplicant = async (comment) => {
    const originalApplicantHistory = applicantHistory ? [...applicantHistory] : null;
    const updatedHistory = applicantHistory ? [...applicantHistory] : [];
    let creatingNew = false;

    if (!comment.commentId) {
      creatingNew = true;
      updatedHistory.unshift({
        id: 0,
        title: comment.commentTitle,
        message: comment.commentMessage,
        rating: comment.rating,
        type: 'comment',
      });
    } else {
      const commentIndex = updatedHistory.findIndex((x) => x.id === comment.commentId);
      if (commentIndex !== -1) {
        updatedHistory[commentIndex] = {
          ...updatedHistory[commentIndex],
          title: comment.commentTitle,
          message: comment.commentMessage,
          rating: comment.rating,
        };
      }
    }
    setApplicantHistory(updatedHistory);
    comment.applicantId = applicantId;
    if (!comment.commentId) {
      comment.commentId = 0;
    }

    try {
      const response = await applicantService.addOrUpdateCommentToApplicant(comment);
      if (creatingNew && response?.data) {
        const updatedComment = response.data[0];
        const toUpdateHistory = [...updatedHistory];
        toUpdateHistory[0] = {
          ...toUpdateHistory[0],
          id: updatedComment.id,
          userFirstName: updatedComment.userFirstName,
          userLastName: updatedComment.userLastName,
          rating: updatedComment.rating,
        };
        setApplicantHistory(toUpdateHistory);
      }
    } catch (error) {
      setApplicantHistory(originalApplicantHistory);
    }
  };

  // Delete a comment after confirming via SweetAlert
  const deleteComment = (item) => {
    swal(utils.getWarningSwal()).then((willDelete) => {
      if (willDelete) {
        const currentHistory = applicantHistory;
        const newHistory = applicantHistory.filter((historyItem) => historyItem.id !== item.id);
        setApplicantHistory(newHistory);

        applicantService.deleteComment(item.id).then(
          () => {},
          () => {
            setApplicantHistory(currentHistory);
          }
        );
      }
    });
  };

  // Delete an item from applicant history by its id
  const deleteFromApplicantHistory = (id) => {
    setApplicantHistory((prevHistory) => prevHistory.filter((item) => item.id !== id));
  };

  // Placeholder for sorting function
  const sortingChanged = async (columnToSort) => {
    // Implement sorting logic if needed
  };

  // Effect to mimic componentDidMount: refresh data and listen for history changes.
  useEffect(() => {
    refreshData();

    const searchParams = new URLSearchParams(location.search);
    const tab = searchParams.get('tab') || 'applicationDetails';
    setActiveTab(tab);

    const unlisten = history.listen((newLocation, action) => {
      const searchParams = new URLSearchParams(newLocation.search);
      const tab = searchParams.get('tab') || 'applicationDetails';
      if (action === 'POP') {
        setActiveTab(tab);
      }
    });
    return () => {
      if (typeof unlisten === 'function') {
        unlisten();
      }
    };
  }, []);

  // Effect to mimic componentDidUpdate on location.search change (ignoring tab-only changes)
  useEffect(() => {
    if (prevLocationSearchRef.current !== location.search) {
      const oldSearchParams = new URLSearchParams(prevLocationSearchRef.current);
      const newSearchParams = new URLSearchParams(location.search);
      const oldTab = oldSearchParams.get('tab');
      const newTab = newSearchParams.get('tab');

      if (oldTab !== newTab) {
        prevLocationSearchRef.current = location.search;
        return;
      }
      setLoading(true);
      const parsed = queryString.parse(location.search);
      if (parsed.applicationId) {
        getApplication(parsed.applicationId).then(({ data: applicationData }) => {
          setApplication(applicationData);
          setLoading(false);
        });
      } else if (parsed.applicantId) {
        applicantService.getApplicant(parsed.applicantId).then(({ data: applicant }) => {
          setApplication((prev) => ({ ...prev, applicant }));
          setLoading(false);
        });
      }
      prevLocationSearchRef.current = location.search;
    }
  }, [location.search]);

  // Prepare the JSX for single application details
  const applicationsWithNotDeletedStatus = (application.applicant.applications || []).filter(
    (obj) => obj.applicationStatus !== 'Deleted'
  );
  const parsed = queryString.parse(location.search);
  const appId = parsed.applicationId;

  const ratingUpdateRef = useRef(null);

  const updateQuestionRating = (rating, questionId, answerId) => {
    setApplication((prevApp) => {
      if (!prevApp.answers || !Array.isArray(prevApp.answers)) return prevApp;

      // Save previous answers for rollback in case the API call fails.
      ratingUpdateRef.current = prevApp.answers;

      // Create the new answers array with the updated rating.
      const updatedAnswers = prevApp.answers.map((answer) =>
        answer.questionId === questionId ? { ...answer, rating } : answer
      );
      // Build the updated application object.
      const updatedApp = { ...prevApp, answers: updatedAnswers };

      // Calculate the fullScore using the updated application object.
      const fullScorePercentage = utils.calculateFullScore(updatedApp);
      // Immediately update the local score state.

      // Call the API to update the rating in the backend.
      addRatingToApplicantsAnswer(
        rating,
        questionId,
        answerId,
        updatedApp.id,
        fullScorePercentage,
        updatedApp.score?.id
      )
        .then((response) => {
          // Update the job with the new data from the response
          setApplication((current) => ({
            ...current,
            score: {
              ...current.score,
              fullScorePercentage: response.data.fullScorePercentage,
            },
          }));
        })
        .catch(() => {
          // Roll back the state if the API call fails.
          setApplication((current) => {
            if (ratingUpdateRef.current) {
              return { ...current, answers: ratingUpdateRef.current };
            }
            return current;
          });
        });

      return updatedApp;
    });
  };

  const SingleApplicationDetails = (
    <>
      {applicationsWithNotDeletedStatus.length > 0 && !appId ? (
        <ApplicationsTable
          applications={applicationsWithNotDeletedStatus}
          forSpecificApplicant={true}
          refreshStatus={refreshData}
          onSortingChanged={(columnToSort) => sortingChanged(columnToSort)}
        />
      ) : (
        <>
          {application.created ? (
            <>
              {application.job ? (
                <ApplicationDisplay
                  application={application}
                  t={t}
                  refreshData={refreshData}
                  i18nLanguage={i18n.language}
                  downloadCv={downloadDocument}
                  updateQuestionRating={updateQuestionRating}
                />
              ) : (
                <>
                  <h4>{t('applications.APPLICATION_CREATED_FROM_COMPETENCES')}</h4>
                  <br />
                  <Row>
                    <Col>
                      {t('applicants.CV')}:&nbsp;&nbsp;
                      <Button
                        id="btnOpenCv"
                        color="primary"
                        onClick={() => {
                          downloadDocument(
                            application.cv?.url,
                            application.applicant.firstName + '_' + application.applicant.lastName + '_CV',
                            t
                          );
                        }}
                      >
                        <b>{t('applicants.VIEW_CV')}</b>
                      </Button>
                    </Col>
                  </Row>
                  <br />
                  <h4>
                    <strong>{t('competences.COMPETENCES')}:</strong>
                  </h4>
                  {application.applicantJobCompetences &&
                    application.applicantJobCompetences.map((item, index) => (
                      <form key={index} className="form-horizontal">
                        <div>
                          <b>{t('competences.COMPETENCE_NAME')}:</b> {item.competenceName}
                        </div>
                        <div>
                          <b>{t('modals.ESTIMATED_EXPERIENCE')}:</b> {item.estimatedExperience}
                        </div>
                        <div>
                          <b>{t('common.EXPLANATION')}:</b> {item.explanation}
                        </div>
                      </form>
                    ))}
                </>
              )}
            </>
          ) : (
            <h3 className="p-4 m-4">{t('applications.NO_APPLICATIONS_FOR_APPLICANT')}</h3>
          )}
        </>
      )}
    </>
  );

  return (
    <ContentWrapper>
      {loading ? (
        <SpinnerLoader />
      ) : (
        <Row>
          <Col lg="4">
            <>
              <ApplicantBasicCard
                applicant={application.applicant}
                onStatusRefresh={refreshData}
                updateFeOnly={updateFeOnly}
              />
              <CommentsCard
                comments={applicantHistory?.filter((item) => item.type === 'comment')}
                applicantId={application.applicant.id}
                onDelete={deleteComment}
                addOrUpdateComment={addOrUpdateCommentToApplicant}
              />
            </>
          </Col>
          <Col lg="8">
            <div className="card card-default">
              <div role="tabpanel">
                <Nav tabs justified>
                  <NavItem style={{ cursor: 'pointer' }}>
                    <NavLink
                      className={activeTab === 'applicationDetails' ? 'active' : ''}
                      onClick={() => toggleTab('applicationDetails')}
                    >
                      <em className="fa-2x icon-envelope-letter mr-2"> {t('applications.APPLICATION_S')}</em>
                    </NavLink>
                  </NavItem>
                  <NavItem style={{ cursor: 'pointer' }}>
                    <NavLink
                      className={activeTab === 'applicantTimeline' ? 'active' : ''}
                      onClick={() => toggleTab('applicantTimeline')}
                    >
                      <em className="fa-2x icon-graph mr-2"> {t('applications.APPLICANT_TIMELINE')}</em>
                    </NavLink>
                  </NavItem>
                  <NavItem style={{ cursor: 'pointer' }}>
                    <NavLink
                      className={activeTab === 'applicantNotes' ? 'active' : ''}
                      onClick={() => toggleTab('applicantNotes')}
                    >
                      <em className="fa-2x icon-note mr-2"> {t('applications.APPLICANT_NOTES')}</em>
                    </NavLink>
                  </NavItem>
                  {/* <NavItem style={{ cursor: 'pointer' }}>
                    <NavLink
                      className={activeTab === 'applicantDetails' ? 'active' : ''}
                      onClick={() => toggleTab('applicantDetails')}
                    >
                      <em className="fa-2x icon-user mr-2"> {t('applicants.APPLICANT_DETAILS')}</em>
                    </NavLink>
                  </NavItem> */}
                </Nav>
                <TabContent activeTab={activeTab}>
                  <TabPane tabId="applicationDetails" role="tabpanel">
                    {SingleApplicationDetails}
                  </TabPane>
                  <TabPane tabId="applicantTimeline" role="tabpanel">
                    <CommentsAndHistoryTimeline
                      id="fullTimelineId"
                      key="fullTimelineKey"
                      applicantId={application.applicant.id}
                      applicantHistory={applicantHistory}
                      deleteFromApplicantHistory={deleteFromApplicantHistory}
                      updateApplicantHistory={addOrUpdateCommentToApplicant}
                      loadComments={false}
                    />
                  </TabPane>
                  <TabPane tabId="applicantNotes" role="tabpanel">
                    <CommentsAndHistoryTimeline
                      id="commentsTimelineId"
                      key="commentsTimelineKey"
                      applicantId={application.applicant.id}
                      applicantHistory={applicantHistory?.filter((item) => item.type === 'comment')}
                      deleteFromApplicantHistory={deleteFromApplicantHistory}
                      updateApplicantHistory={addOrUpdateCommentToApplicant}
                      loadComments={false}
                    />
                  </TabPane>
                  {/* <TabPane tabId="applicantDetails" role="tabpanel">
                    <ApplicantDetails applicant={application.applicant} />
                  </TabPane> */}
                </TabContent>
              </div>
            </div>
          </Col>
        </Row>
      )}
      <a hidden download={fileName} href={application.fileDownloadUrl} ref={fileDownloadRef}></a>
    </ContentWrapper>
  );
};

export default ApplicationDetails;
